import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Flame, Play, Square, Music, VolumeX, ArrowLeft, Crown,
  Feather, Zap, Eye, Ear, Hand, UtensilsCrossed, Wind, Sparkles, SkipForward,
} from "lucide-react";
import { TAVERNE } from "@/constants/testIds";
import { taverneList, taverneUsage, taverneListen } from "@/lib/api";
import { useAccount } from "@/lib/account";
import { useLang } from "@/lib/lang";

// Royalty-free celtic tracks (Kevin MacLeod — CC BY 4.0), served locally by
// FastAPI so they work in any preview / production environment. Attribution
// shown at the bottom of the page.
const API_ROOT = process.env.REACT_APP_BACKEND_URL || "";
const MUSIC_TRACKS = [
  { id: "celtic_impulse", label: "Celtic Impulse", url: `${API_ROOT}/api/static/audio/celtic_impulse.mp3` },
  { id: "immersed", label: "Immersed", url: `${API_ROOT}/api/static/audio/immersed.mp3` },
  { id: "lord_of_the_land", label: "Lord of the Land", url: `${API_ROOT}/api/static/audio/lord_of_the_land.mp3` },
];

const SENSE_ICON = {
  vue: Eye,
  ouie: Ear,
  toucher: Hand,
  gout: UtensilsCrossed,
  odorat: Wind,
};

export default function Taverne() {
  const { lang, t, setLang } = useLang();
  const { email, status, refresh } = useAccount() || {};
  const [poems, setPoems] = useState([]);
  const [filter, setFilter] = useState("all"); // all | reflexif | slam | satirique
  const [usage, setUsage] = useState({ used_today: 0, limit: null, premium_active: true, remaining: null });
  const [musicOn, setMusicOn] = useState(false);
  const [trackIdx, setTrackIdx] = useState(0);
  const [speakingId, setSpeakingId] = useState(null);
  const audioRef = useRef(null);
  const utteranceRef = useRef(null);

  // Load poems whenever lang changes
  useEffect(() => {
    let cancelled = false;
    taverneList(lang)
      .then((r) => { if (!cancelled) setPoems(r.poems || []); })
      .catch(() => { if (!cancelled) setPoems([]); });
    return () => { cancelled = true; };
  }, [lang]);

  // Load usage counter
  useEffect(() => {
    let cancelled = false;
    taverneUsage(email)
      .then((r) => { if (!cancelled) setUsage(r); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [email, status?.premium_active]);

  // Cleanup speech on unmount
  useEffect(() => {
    return () => {
      try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
      if (audioRef.current) { try { audioRef.current.pause(); } catch { /* ignore */ } }
    };
  }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return poems;
    if (filter === "reflexif") return poems.filter((p) => p.category === "reflexif");
    if (filter === "slam") return poems.filter((p) => p.category === "slam");
    if (filter === "satirique") return poems.filter((p) => p.category === "slam" && p.sub_tone === "satirique");
    return poems;
  }, [poems, filter]);

  const toggleMusic = () => {
    if (!audioRef.current) return;
    if (musicOn) {
      audioRef.current.pause();
      setMusicOn(false);
    } else {
      audioRef.current.volume = 0.28;
      audioRef.current.loop = false; // we chain tracks manually via onended
      const p = audioRef.current.play();
      if (p && p.catch) {
        p.then(() => setMusicOn(true)).catch(() => {
          toast.error(lang === "fr" ? "Musique indisponible pour le moment." : "Music unavailable right now.");
        });
      } else {
        setMusicOn(true);
      }
    }
  };

  const nextTrack = () => {
    setTrackIdx((i) => (i + 1) % MUSIC_TRACKS.length);
  };

  // When the current track ends, chain into the next one seamlessly.
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const handler = () => {
      const nextIdx = (trackIdx + 1) % MUSIC_TRACKS.length;
      setTrackIdx(nextIdx);
    };
    el.addEventListener("ended", handler);
    return () => el.removeEventListener("ended", handler);
  }, [trackIdx]);

  // Whenever trackIdx changes AND music is on, keep playing
  useEffect(() => {
    const el = audioRef.current;
    if (!el || !musicOn) return;
    el.load();
    el.play().catch(() => { /* browser blocked, will require user gesture */ });
  }, [trackIdx, musicOn]);

  const stopSpeaking = () => {
    try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
    setSpeakingId(null);
  };

  const speak = async (poem) => {
    if (!("speechSynthesis" in window)) {
      toast.error(lang === "fr"
        ? "Votre navigateur ne supporte pas la lecture audio."
        : "Your browser does not support voice playback.");
      return;
    }
    // Record listen server-side (rate-limited)
    try {
      const r = await taverneListen(poem.id, email);
      setUsage(r);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 429 && detail?.code === "taverne_daily_limit") {
        toast.error(t("taverne.usage.limit"));
        return;
      }
      toast.error(t("common.error.retry"));
      return;
    }

    // If any speech currently playing, stop it first.
    stopSpeaking();

    const utter = new SpeechSynthesisUtterance(poem.text);
    utter.lang = lang === "en" ? "en-GB" : "fr-FR";
    utter.rate = 0.82;   // slower for bardic effect
    utter.pitch = 0.55;  // clearly masculine, deep bardic voice
    utter.volume = 1.0;
    // Try to pick a clearly male voice if available
    try {
      const voices = window.speechSynthesis.getVoices();
      const langPrefix = utter.lang.slice(0, 2);
      const preferred =
        voices.find((v) => v.lang?.startsWith(langPrefix) && /thomas|daniel|arthur|nicolas|paul|henri|antoine|luc|male|homme/i.test(v.name))
        || voices.find((v) => v.lang?.startsWith(langPrefix) && !/female|femme|amelie|marie|julie|virginie/i.test(v.name))
        || voices.find((v) => v.lang?.startsWith(langPrefix));
      if (preferred) utter.voice = preferred;
    } catch { /* ignore */ }

    utter.onend = () => setSpeakingId((cur) => (cur === poem.id ? null : cur));
    utter.onerror = () => setSpeakingId((cur) => (cur === poem.id ? null : cur));
    utteranceRef.current = utter;
    setSpeakingId(poem.id);
    window.speechSynthesis.speak(utter);
    if (refresh) refresh();
  };

  return (
    <div data-testid={TAVERNE.page} className="taverne-root min-h-screen relative">
      {/* Background music (hidden audio element) */}
      <audio ref={audioRef} src={MUSIC_TRACKS[trackIdx].url} preload="none" crossOrigin="anonymous" />

      {/* Ambient tavern backdrop */}
      <div className="taverne-bg" aria-hidden="true">
        <div className="taverne-vignette" />
        <div className="taverne-embers" />
      </div>

      {/* Top bar */}
      <header className="relative z-10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-[0.24em]"
                style={{ color: "var(--gold)" }}>
            <ArrowLeft size={14} /> {t("taverne.back")}
          </Link>
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center rounded-full text-[11px] font-mono overflow-hidden"
                 style={{ background: "rgba(107, 63, 160, 0.25)", border: "1px solid rgba(214, 193, 255, 0.25)" }}>
              <button onClick={() => setLang("fr")}
                      className="px-2.5 py-1.5 transition-colors"
                      style={{ background: lang === "fr" ? "var(--gold)" : "transparent",
                               color: lang === "fr" ? "var(--void)" : "var(--violet-soft)",
                               fontWeight: lang === "fr" ? 600 : 400 }}
                      data-testid="taverne-lang-fr" type="button">FR</button>
              <button onClick={() => setLang("en")}
                      className="px-2.5 py-1.5 transition-colors"
                      style={{ background: lang === "en" ? "var(--gold)" : "transparent",
                               color: lang === "en" ? "var(--void)" : "var(--violet-soft)",
                               fontWeight: lang === "en" ? 600 : 400 }}
                      data-testid="taverne-lang-en" type="button">EN</button>
            </div>
            <button onClick={toggleMusic} data-testid={TAVERNE.musicToggle}
                    type="button"
                    className="inline-flex items-center gap-2 text-[11px] font-mono px-3 py-2 rounded-full transition-all"
                    style={{ background: musicOn ? "rgba(212,175,55,0.18)" : "rgba(107,63,160,0.22)",
                             border: "1px solid rgba(214,193,255,0.25)",
                             color: musicOn ? "var(--gold)" : "var(--violet-soft)" }}>
              {musicOn ? <VolumeX size={13} /> : <Music size={13} />}
              {t("taverne.music")} · {musicOn ? t("taverne.music.on") : t("taverne.music.off")}
            </button>
            {musicOn && (
              <button onClick={nextTrack} data-testid="taverne-music-next"
                      type="button"
                      title={`${MUSIC_TRACKS[trackIdx].label} → ${MUSIC_TRACKS[(trackIdx + 1) % MUSIC_TRACKS.length].label}`}
                      className="hidden md:inline-flex items-center gap-1 text-[11px] font-mono px-3 py-2 rounded-full transition-all"
                      style={{ background: "rgba(6,4,12,0.55)",
                               border: "1px solid rgba(214,193,255,0.25)",
                               color: "var(--violet-soft)" }}>
                <SkipForward size={13} /> {MUSIC_TRACKS[trackIdx].label}
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pt-6 sm:pt-10 pb-10">
        <div className="overline mb-4">
          <span className="kicker-line" />
          {t("taverne.kicker")}
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
          {t("taverne.title.a")}
          <span className="italic" style={{ color: "var(--gold-glow)" }}>{t("taverne.title.b")}</span>
          {t("taverne.title.c")}
        </h1>
        <p className="mt-5 max-w-3xl text-base sm:text-lg" style={{ color: "var(--violet-soft)" }}>
          {t("taverne.subtitle")}
        </p>

        {/* Usage counter */}
        <div data-testid={TAVERNE.usage}
             className="mt-8 inline-flex items-center gap-3 text-xs font-mono px-4 py-2.5 rounded-full"
             style={{ background: "rgba(6, 4, 12, 0.55)",
                      border: "1px solid rgba(212, 175, 55, 0.35)",
                      color: "var(--champagne)" }}>
          <Flame size={13} className="text-gold" />
          {usage.premium_active ? (
            <><Crown size={12} /> {t("taverne.usage.premium")}</>
          ) : (
            <>
              {usage.used_today ?? 0} / {usage.limit ?? 5} · {t("taverne.usage.free")}
            </>
          )}
        </div>

      </section>

      {/* Filters */}
      <section className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-6">
        <div className="flex flex-wrap gap-2">
          {[
            ["all", TAVERNE.filterAll, t("taverne.filter.all"), Sparkles],
            ["reflexif", TAVERNE.filterReflexif, t("taverne.filter.reflexif"), Feather],
            ["slam", TAVERNE.filterSlam, t("taverne.filter.slam"), Zap],
            ["satirique", TAVERNE.filterSatirique, t("taverne.filter.satirique"), Flame],
          ].map(([key, tid, label, Icon]) => (
            <button key={key} onClick={() => setFilter(key)} data-testid={tid}
                    type="button"
                    className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] px-4 py-2.5 rounded-full transition-all"
                    style={{
                      background: filter === key ? "var(--gold)" : "rgba(6,4,12,0.55)",
                      color: filter === key ? "var(--void)" : "var(--violet-soft)",
                      border: filter === key ? "1px solid var(--gold)" : "1px solid rgba(214,193,255,0.22)",
                      fontWeight: filter === key ? 600 : 400,
                    }}>
              <Icon size={12} /> {label}
            </button>
          ))}
        </div>
      </section>

      {/* Poems grid */}
      <section className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-10">
        {filtered.length === 0 ? (
          <div className="text-sm font-mono py-16 text-center" style={{ color: "var(--muted)" }}>
            {t("taverne.empty")}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {filtered.map((poem) => (
              <PoemCard
                key={poem.id}
                poem={poem}
                lang={lang}
                t={t}
                speaking={speakingId === poem.id}
                onPlay={() => speak(poem)}
                onStop={stopSpeaking}
                canPlay={usage.premium_active || (usage.remaining ?? 0) > 0 || speakingId === poem.id}
              />
            ))}
          </div>
        )}
      </section>

      {/* Attribution (CC BY 4.0 for Kevin MacLeod music) */}
      <footer className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pb-16 text-[11px] font-mono" style={{ color: "var(--muted)" }}>
        <div className="pt-6 border-t" style={{ borderColor: "rgba(214,193,255,0.12)" }}>
          {lang === "fr" ? (
            <>
              Musique celtique : <em>Celtic Impulse</em>, <em>Immersed</em>, <em>Lord of the Land</em> par
              {" "}<a href="https://incompetech.com" target="_blank" rel="noopener noreferrer" style={{ color: "var(--gold)" }}>Kevin MacLeod</a>
              {" "}—{" "}
              <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener noreferrer" style={{ color: "var(--gold)" }}>CC BY 4.0</a>.
              Voix bardique générée par la synthèse vocale de votre navigateur (Web Speech API).
            </>
          ) : (
            <>
              Celtic music: <em>Celtic Impulse</em>, <em>Immersed</em>, <em>Lord of the Land</em> by
              {" "}<a href="https://incompetech.com" target="_blank" rel="noopener noreferrer" style={{ color: "var(--gold)" }}>Kevin MacLeod</a>
              {" "}—{" "}
              <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener noreferrer" style={{ color: "var(--gold)" }}>CC BY 4.0</a>.
              Bard voice generated by your browser's speech synthesis (Web Speech API).
            </>
          )}
        </div>
      </footer>

      {/* Tavern styles */}
      <style>{`
        .taverne-root {
          background: radial-gradient(ellipse at top, #1e0f04 0%, #0b0603 60%, #05030a 100%);
          color: #f6ecd6;
          overflow-x: hidden;
        }
        .taverne-bg {
          position: fixed; inset: 0; z-index: 0; pointer-events: none;
          background:
            repeating-linear-gradient(90deg, rgba(0,0,0,0.28) 0, rgba(0,0,0,0.28) 2px, transparent 2px, transparent 90px),
            repeating-linear-gradient(0deg, rgba(0,0,0,0.14) 0, rgba(0,0,0,0.14) 1px, transparent 1px, transparent 26px),
            radial-gradient(circle at 15% 25%, rgba(255,138,60,0.22) 0%, transparent 42%),
            radial-gradient(circle at 85% 78%, rgba(255,72,20,0.16) 0%, transparent 40%),
            linear-gradient(180deg, #180c05 0%, #0a0603 100%);
        }
        .taverne-vignette {
          position: absolute; inset: 0;
          background: radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.6) 100%);
        }
        .taverne-embers {
          position: absolute; inset: 0;
          background-image:
            radial-gradient(circle at 12% 82%, rgba(255,148,50,0.85) 0.4px, transparent 1px),
            radial-gradient(circle at 78% 18%, rgba(255,196,80,0.6) 0.4px, transparent 1px),
            radial-gradient(circle at 44% 65%, rgba(255,130,40,0.5) 0.4px, transparent 1px),
            radial-gradient(circle at 62% 92%, rgba(255,180,70,0.7) 0.4px, transparent 1px);
          animation: emberFloat 9s ease-in-out infinite;
          opacity: 0.7;
        }
        @keyframes emberFloat {
          0%,100% { transform: translateY(0); opacity: 0.6; }
          50%     { transform: translateY(-8px); opacity: 0.95; }
        }
        .tav-card {
          background:
            linear-gradient(180deg, rgba(28,16,7,0.9) 0%, rgba(14,7,3,0.95) 100%);
          border: 1px solid rgba(212, 175, 55, 0.24);
          border-radius: 22px;
          padding: 24px;
          position: relative;
          overflow: hidden;
          transition: transform 260ms ease, border-color 260ms ease, box-shadow 260ms ease;
        }
        .tav-card::before {
          content: ""; position: absolute; inset: 0; pointer-events: none;
          background: repeating-linear-gradient(45deg, rgba(255,255,255,0.02) 0 3px, transparent 3px 8px);
        }
        .tav-card:hover {
          transform: translateY(-4px);
          border-color: rgba(212, 175, 55, 0.55);
          box-shadow: 0 24px 48px -20px rgba(255, 138, 44, 0.35), 0 0 0 1px rgba(212,175,55,0.15);
        }
        .tav-card.speaking {
          border-color: rgba(212, 175, 55, 0.85);
          box-shadow: 0 0 0 1px rgba(212,175,55,0.45), 0 24px 52px -18px rgba(255,138,44,0.55);
        }
        .tav-poem {
          white-space: pre-wrap;
          font-family: "Cormorant Garamond", "Georgia", serif;
          font-size: 17px;
          line-height: 1.7;
          color: #f0e4c8;
        }
      `}</style>
    </div>
  );
}

function PoemCard({ poem, lang, t, speaking, onPlay, onStop, canPlay }) {
  const Category = poem.category === "slam" ? Zap : Feather;
  const SenseIcon = poem.sense ? SENSE_ICON[poem.sense] : null;
  const chip = poem.category === "slam"
    ? (poem.sub_tone === "satirique" ? t("taverne.subcat.satirique") : t("taverne.subcat.thematique"))
    : (poem.sense ? t(`taverne.sense.${poem.sense}`) : t("taverne.category.reflexif"));

  return (
    <article data-testid={TAVERNE.card(poem.id)} className={`tav-card ${speaking ? "speaking" : ""}`}>
      <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.24em]" style={{ color: "var(--gold)" }}>
        <Category size={11} />
        <span>{poem.category === "slam" ? t("taverne.category.slam") : t("taverne.category.reflexif")}</span>
        <span style={{ color: "rgba(214,193,255,0.55)" }}>·</span>
        <span className="inline-flex items-center gap-1" style={{ color: "var(--violet-soft)" }}>
          {SenseIcon && <SenseIcon size={11} />} {chip}
        </span>
      </div>

      <h3 className="mt-3 font-serif text-2xl leading-tight" style={{ color: "#fff5df" }}>
        {poem.title}
      </h3>
      {poem.subtitle && (
        <p className="mt-1 text-sm italic" style={{ color: "var(--violet-soft)" }}>
          {poem.subtitle}
        </p>
      )}

      <div className="mt-4 tav-poem">
        {poem.text}
      </div>

      <div className="mt-5 flex items-center gap-2">
        {!speaking ? (
          <button onClick={onPlay} disabled={!canPlay}
                  data-testid={TAVERNE.play(poem.id)}
                  type="button"
                  className="inline-flex items-center gap-2 text-sm font-mono px-4 py-2 rounded-full transition-all disabled:opacity-40"
                  style={{ background: "var(--gold)", color: "var(--void)", fontWeight: 600 }}>
            <Play size={13} /> {t("taverne.play")}
          </button>
        ) : (
          <button onClick={onStop}
                  data-testid={TAVERNE.stop(poem.id)}
                  type="button"
                  className="inline-flex items-center gap-2 text-sm font-mono px-4 py-2 rounded-full transition-all"
                  style={{ background: "rgba(212,175,55,0.15)", color: "var(--gold)",
                           border: "1px solid rgba(212,175,55,0.5)" }}>
            <Square size={13} /> {t("taverne.stop")}
          </button>
        )}
        {speaking && (
          <span className="text-[11px] font-mono animate-pulse" style={{ color: "var(--gold)" }}>
            {t("taverne.playing")}
          </span>
        )}
      </div>
    </article>
  );
}
